import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BackgroundMode } from '@ionic-native/background-mode/ngx';
import { InAppBrowser, InAppBrowserOptions } from '@ionic-native/in-app-browser/ngx';
import { AlertController, LoadingController, MenuController, ModalController, Platform } from '@ionic/angular';
import { interval, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PreserviceAlertComponent } from '../components/custom-alerts/preservice-alert/preservice-alert.component';
import { ForgotPasswordComponent } from '../components/modal-forms/forgot-password/forgot-password.component';
import { LoginModalComponent } from '../components/modal-forms/login-modal/login-modal.component';
import { PinModalComponent } from '../components/modal-forms/pin-modal/pin-modal.component';
import { AppSettingsComponent } from '../components/modals/app-settings/app-settings.component';
import { EtaSelectorComponent } from '../components/modals/eta-selector/eta-selector.component';
import { InfoComponent } from '../components/modals/info/info.component';
import { Bump_screen } from '../models/bump-screen.model';
import { Order, FoodDrinkType } from '../models/order.model';
import { AuthService } from './core/auth/auth.service';
import { CoreService, SERVER_TYPE } from './core/core/core.service';
import { DocketFilterService, IFilterObject, IPreorderWeek } from './core/docket-filter/docket-filter.service';
import { CONTEXT_TYPE, DocketService } from './core/docket/docket.service';
import { VipService } from './core/insights/vip.service';
import { OpeningHoursService } from './core/opening-hours/opening-hours.service';
import { PRINT_TRIGGER } from './core/printer/printer.constants';
import { PrinterService } from './core/printer/printer.service';
import { AppConfigService } from './core/settings/app-config.service';
import { SettingsService } from './core/settings/settings.service';
import { WalkthroughService } from './core/walkthrough/walkthrough.service';
import { AnalyticsService } from './utils/analytics/analytics.service';
import { HubAlertModule } from './utils/prompt-queue/alert-queue.module.constant';
import { AlertQueueService } from './utils/prompt-queue/alert-queue.service';
import { StorageService } from './utils/storage/storage.service';
import {
  APP_DOCKET_DISPLAY_UPDATE,
  APP_LOADED,
  APP_SETTINGS_CHANGED,
  AUTO_ETA_PRINTING_ORDER,
  AUTO_PRINTING_ORDER,
  DAY_CHANGED,
  ETA_PROMPT,
  FILTER_CHANGED,
  OPEN_URL_BROWSER,
  POLL_UPDATE,
  SERVER_CHANGED,
  VIEW_MESSAGE_BOARD_ITEM,
} from './utils/watcher/watcher-event';
import { WatcherService } from './utils/watcher/watcher.service';

import { datadogRum } from '@datadog/browser-rum';
import { PrinterGroup } from '../models/printer/printer-group.model';
import { Printer } from '../models/printer/printer.model';
import { IMessageBoardConfig, MESSAGE_BOARD_ITEM_TYPE, MessageBoardItem } from '../models/utils/message-board.model';
import { IPrinterJobQueue, IPrintResponse, IPrintStringObj } from './core/printer/printer.interface';
import { MessageBoardService } from './utils/message-board/message-board.service';

import * as moment from 'moment-timezone';
import { DispatchBumpService } from './core/docket/dispatchers/bump/dispatch.bump.service';
import { DispatchEtaService } from './core/docket/dispatchers/eta/dispatch.eta.service';
import { DispatchPrintedService } from './core/docket/dispatchers/printed/dispatch.printed.service';
import { DispatchSmsService } from './core/docket/dispatchers/sms/dispatch.sms.service';

import cloneDeep from 'lodash/cloneDeep';
import each from 'lodash/each';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import forOwn from 'lodash/forOwn';
import has from 'lodash/has';
import map from 'lodash/map';
import size from 'lodash/size';
import { DocketLayoutConfigService, IFormattedDocket } from './core/docket/layout/docket.layout.config.service';
import { GeneratedDocketRepositoryService } from './core/docket/repository/generated-docket-repository.service';
import { FeatureFlagList, FeatureFlagsService } from './core/settings/feature-flags.service';
import { UtilityClass } from './utils/UtilityClass';

@Injectable({
  providedIn: 'root',
})
export class MainService {
  /** Timed events subscriptions */
  private _timerAutoPrint: Subscription;

  /** The code push alert object */
  private _updateAlert: any;

  /** Flagger to check if the code push alert is displayed */
  private _updateAlertShowing: boolean = false;

  /** The ETA selector modal. We need to make sure there's only one active. */
  private _etaModalOpen: any;

  /** Flagger to check preservice orders */
  private _failedPrintedUpdateBacklog: { ids: string[] }[] = [];

  /** Modal for the future order prompt to print */
  private _futureOrderPrompt;

  // to be cleared in unit testing
  public etaPromptSub: Subscription;
  public autoPrintingSub: Subscription;
  public autoETAPrintingSub: Subscription;
  public servicePoll: Subscription;
  public dayChangedSub: Subscription;
  public serverChanged: Subscription;
  public filterChanged: Subscription;
  public updatePollSub: Subscription;
  public appSettingsSub: Subscription;
  public openBrowser: Subscription;
  public messageBoardSubscription: Subscription;

  constructor(
    public authentication: AuthService,
    public storage: StorageService,
    public router: Router,
    public menu: MenuController,
    public modalCtrl: ModalController,
    public docketService: DocketService,
    public vipService: VipService,
    public alertCtrl: AlertController,
    public alertQueue: AlertQueueService,
    public filterService: DocketFilterService,
    public watcher: WatcherService,
    public core: CoreService,
    public platform: Platform,
    public inAppBrowser: InAppBrowser,
    public loadingCtrl: LoadingController,
    public analytics: AnalyticsService,
    public printer: PrinterService,
    public settings: SettingsService,
    public config: AppConfigService,
    public walkthrough: WalkthroughService,
    public openService: OpeningHoursService,
    public backgroundMode: BackgroundMode,
    public messageBoard: MessageBoardService,
    public dispatchEtaService: DispatchEtaService,
    public dispatchBumpService: DispatchBumpService,
    public dispatchPrintedService: DispatchPrintedService,
    public dispatchSmsService: DispatchSmsService,
    public featureFlagService: FeatureFlagsService,
    public generatedDocketService: GeneratedDocketRepositoryService,
    public docketLayoutService: DocketLayoutConfigService,
    @Inject(DOCUMENT) document,
  ) {
    this.messageBoardSubscription = this.watcher.on(VIEW_MESSAGE_BOARD_ITEM).subscribe((data: any) => {
      const { item, role } = data;
      if (item && item instanceof MessageBoardItem) {
        switch (item.Type) {
          case MESSAGE_BOARD_ITEM_TYPE.PRINTER_ERROR:
            this.messageBoard.removeItem(item.ID);

            if (role == 'retry') {
              const order: Order = this.docketService.getOrderById(item.Details.order);
              if (order) {
                this.printRetry(order);
              }
            } else {
              this.messageBoard.hide();
            }
            break;
          default:
          // don't do anything
        }
      }
    });

    this.serverChanged = this.watcher.on(SERVER_CHANGED).subscribe(() => {
      if (datadogRum && this.core.Active_server) {
        datadogRum.addRumGlobalContext('server', this.core.Active_server.label);
      }
    });

    // DO NOT COMBINE FILTER_CHANGED and POLL_UPDATE
    // SHOULD HAVE DIFFERENT DEBOUNCETIME
    this.filterChanged = this.watcher
      .on(FILTER_CHANGED)
      .pipe(debounceTime(50))
      .subscribe(() => {
        this.updateDisplayedOrderItemsAndCount();
      });

    // there is a duplicate event but with different debounce time
    // leave it as is, this is to control burst of orders and refresh cycle
    this.updatePollSub = this.watcher
      .on(POLL_UPDATE)
      .pipe(debounceTime(200))
      .subscribe(() => {
        this.updateDisplayedOrderItemsAndCount();
      });

    this.etaPromptSub = this.watcher.on(ETA_PROMPT).subscribe(() => {
      if (this.authentication.Logged_in) {
        // show today's pending orders
        // show the modal selector
        this.checkOrdersWithNoEta();
      }
    });

    this.autoPrintingSub = this.watcher.on(AUTO_PRINTING_ORDER).subscribe(() => {
      if (this.authentication.Logged_in) {
        // auto print Dine in orders
        this.checkDineinOrdersWithNoEta();
      }
    });

    this.autoETAPrintingSub = this.watcher.on(AUTO_ETA_PRINTING_ORDER).subscribe(() => {
      if (this.authentication.Logged_in) {
        // auto set ETA orders
        this.checkAutoSetETAOrdersForToday();
      }
    });

    // listen to every order update - wait for 3 seconds to bundle successive data
    this.servicePoll = this.watcher
      .on(POLL_UPDATE)
      .pipe(debounceTime(3000))
      .subscribe(() => {
        if (this.authentication.Logged_in) {
          // check if there are future day orders to print
          this.checkFutureDayPreOrdersToPrint();
        }
      });

    this.dayChangedSub = this.watcher
      .on(DAY_CHANGED)
      .pipe(debounceTime(1000))
      .subscribe(() => {
        if (this.authentication.Logged_in) {
          // force 1 polling - to remove outdated orders
          this.docketService.requestOrderList();

          // check if there are future day orders to print
          this.checkFutureDayPreOrdersToPrint();

          // refresh the opening hours
          this.openService.getOpeningStatus();

          // refresh the filter list and clear the cache
          this.filterService.createPreorderFilters('YYYY-MM-DD', true, true);

          const now = moment().format('YYYY-MM-DD hh:mm:ss Z');
          const serverTime = this.core.Server_time.format('YYYY-MM-DD hh:mm:ss Z');

          this.analytics.trackEvent(
            'Day Changed',
            `DEVICE TIME: ${now} | SERVER TIME: ${serverTime}`,
            this.authentication.Tracking_key,
          );
        }
      });

    // listen to any setting changes
    this.appSettingsSub = this.watcher.on(APP_SETTINGS_CHANGED).subscribe((setting: any) => {
      // change the dark mode
      if (size(setting) && has(setting, 'general') && has(setting.general, 'dark_mode')) {
        if (setting.general.dark_mode) {
          document.body.classList.add('dark');
        } else {
          document.body.classList.remove('dark');
        }
      }
    });

    this.openBrowser = this.watcher.on(OPEN_URL_BROWSER).subscribe((url: string) => {
      this.openUrl2(url, '_blank');
    });
  }

  /** Fires event changes  to force:
   * 1. Recount on filters
   * 2. Force update on order list
   */
  public updateDisplayedOrderItemsAndCount() {
    this.watcher.broadcast(APP_DOCKET_DISPLAY_UPDATE, 'updateDisplayedOrderItemsAndCount');
    this.docketService.orderFilterCountSummary();
  }

  clearDefaultSub(): void {
    this.watcher.suppressAll([
      this.etaPromptSub,
      this.autoPrintingSub,
      this.autoETAPrintingSub,
      this.servicePoll,
      this.dayChangedSub,
      this.serverChanged,
      this.filterChanged,
      this.updatePollSub,
      this.appSettingsSub,
      this.openBrowser,
      this.messageBoardSubscription,
    ]);
  }

  /**
   * Creates a cycle of timed events
   *
   * 1. Clears the backlog for failed printed value update
   * 2. Checks any dine in orders without ETA to auto print
   */
  startTimedEvents() {
    this.stopTimedEvents();

    // clears auto print backlog every 3 minutes
    this._timerAutoPrint = interval(180000).subscribe(() => {
      if (size(this._failedPrintedUpdateBacklog)) {
        this.clearAutoSetETAPrintBacklog();
      }

      const dineinOrder: Order = this.docketService.getPendingDineInOrderToAutoPrint();
      if (size(dineinOrder)) {
        this.checkDineinOrdersWithNoEta();
      }
    });
  }

  stopTimedEvents() {
    if (this._timerAutoPrint) this._timerAutoPrint.unsubscribe();
  }

  /**
   * Starts the app by checking if there are any stored bump screen storage. If there are none,
   * the app will then redirect to the login screen. If exists, it will verify if the screen access_key
   * has expired and will attempt to re-authenticate, otherwise just continue.
   */
  async start() {
    /** Get all app global settings */
    await this.settings.loadGlobalSettings();

    /**
     * Load the previously selected server
     */
    const server = await this.storage.load('server');

    // if a cached server is no longer in the list, logout
    if (server && Object.values(SERVER_TYPE).indexOf(server) === -1) {
      // reset and logout
      await this.userLogout();

      this.watcher.broadcast(APP_LOADED);

      return;
    }

    this.core.setServerByEnv(server || SERVER_TYPE.PROD_AU_NZ);

    // check if there's a stored authentication key
    const screenLoad = await Promise.all([this.storage.load('screen'), this.storage.load('user_access')]);

    if (size(screenLoad) == 2 && screenLoad[0] && screenLoad[1]) {
      const screen = screenLoad[0];
      const userAccess = screenLoad[1];

      // re-authenticate expired screen
      if (has(screen, 'expiry') && has(screen, 'access_key') && has(userAccess, 'id')) {
        const now = Date.now();
        if (now >= screen.expiry) {
          const authenticate = await this.authentication.authenticate(screen);
          if (!authenticate || (authenticate && !authenticate.success)) {
            // no valid screen available
            await this.userLogout();
          } else {
            this._initStoredScreen(screen, userAccess);
          }
        } else {
          this._initStoredScreen(screen, userAccess);
        }
      } else {
        console.log('NO VALID SCREEN', screenLoad);

        // no valid screen available
        await this.userLogout();
        this.watcher.broadcast(APP_LOADED);
      }
    } else {
      // no valid screen available
      await this.userLogout();
      this.watcher.broadcast(APP_LOADED);
    }

    return;
  }

  /**
   * Initialises the app with stored bump screen data
   * @param screen - The raw bump screen object
   */
  private _initStoredScreen(screen: any, user_id: { id: number; token: string }): void {
    // load the other screens
    this.storage.load('account_screens').then(screens => {
      if (screens) {
        // convert to bump screens
        const initialisedScreens: Bump_screen[] = map(screens, (screen: any) => new Bump_screen(screen));
        this.authentication.saveAllBumpScreens(initialisedScreens);
      }
    });

    // load the user email
    this.storage.load('email').then(email => {
      this.authentication.Email = email;
    });

    this.authentication.userLogin(user_id.id);
    this.authentication.New_user = false;

    this.setBumpScreen(new Bump_screen(screen));
    this.watcher.broadcast(APP_LOADED);
  }

  /**
   * Toggles the activation of the side menu
   * @param forceOpen - Forces the side menu to open
   * @param forceClose - Forces the side menu to close
   */
  public async toggleMainMenu(forceOpen: boolean = false, forceClose: boolean = false): Promise<any> {
    if (forceOpen) {
      this.menu.open();
    } else if (forceClose) {
      this.menu.close();
    } else {
      this.menu.toggle();
    }

    return this.menu.isOpen();
  }

  /**
   * Sets an Order to be active. There will be only 1 active order at a time. This will be the focus
   * of docket details and summary.
   * @param order - The order object to set
   */
  public viewOrder(order: Order): void {
    if (order) {
      this.docketService.setActiveOrderById(order.id);

      // simulate an eta selector
      if (order.context_type == CONTEXT_TYPE.Dine_in) {
        if (!order.eta) {
          this.setETASelectorHandler(order, true, false);
          this.analytics.trackEvent(
            'Acknowledge Order',
            'View Dine In - ' + order.id,
            this.authentication.Tracking_key,
          );
        } else if (order.auto_set_eta && !order.printed) {
          this.dispatchPrintedService.updateDocketPrintStatus([order.id], true);
          this.analytics.trackEvent(
            'Acknowledge Order',
            'View Dine In - ' + order.id,
            this.authentication.Tracking_key,
          );
        }
      } else if (order.context_type != CONTEXT_TYPE.Dine_in && order.auto_set_eta && !order.printed) {
        this.dispatchPrintedService.updateDocketPrintStatus([order.id], true);
        this.analytics.trackEvent(
          'Acknowledge Order',
          'View Auto Set ETA - ' + order.id,
          this.authentication.Tracking_key,
        );
      }
    }
  }

  /** AUTHENTICATION */
  /**
   * Sets the active bump screen and initialises all relevant services.
   * @param screen - The bump screen to activate
   */
  async setBumpScreen(screen: Bump_screen): Promise<boolean> {
    return new Promise(resolve => {
      this.enableBackgroundMode();

      // set the key first
      this.storage.globalKey = screen.id;
      this.authentication.setBumpScreen(screen);

      // adjust the feature flags
      this.featureFlagService.setFeatureFlag(FeatureFlagList.BFF, size(this.core.Active_server.bff.url) > 0);
      this.featureFlagService.setFeatureFlag(FeatureFlagList.DOCKET_LAYOUT, size(this.core.Active_server.bff.url) > 0);

      // check if there are any walkthroughs to view
      this.walkthrough.needWalkthrough().then(needWalkthrough => {
        if (needWalkthrough) {
          setTimeout(() => {
            this.navigateTo(['/walkthrough']);
            resolve(true);
          }, 500);
        } else {
          // Load the settings and initialise the service
          this.jumpstartApp().then(done => resolve(true));
        }
      });
    });
  }

  jumpstartApp(): Promise<boolean> {
    return new Promise(async resolve => {
      // always route to home page
      this.navigateTo(['/home']);

      // add a delay - so we don't kill the home page enter animation
      await new Promise(resolve => setTimeout(resolve, 1000));

      // now that we have the screen timezone, recreate the calendar filter
      this.filterService.createPreorderFilters('YYYY-MM-DD', true, true);

      // load and apply screen settings
      await this._loadScreenSettings();

      // update the context filter list
      this.filterService.showContextFilters(this.authentication.Active_bump_screen.availableContextModules);

      // load the docket config
      if (
        this.featureFlagService.isFeatureFlagEnabled(FeatureFlagList.BFF) &&
        this.featureFlagService.isFeatureFlagEnabled(FeatureFlagList.DOCKET_LAYOUT)
      ) {
        try {
          await UtilityClass.asyncCallWithTimeout(this.docketLayoutService.getDocketLayoutConfiguration(), 10000);
        } catch (e) {
          this.analytics.trackEvent('Docket Config', 'Init Failed:' + e.message, this.authentication.Tracking_key);
        }
      }

      // do all the other configurations here
      this.docketService.init();
      this.vipService.init();
      this.openService.getOpeningStatus();

      // works like a cron task
      this.startTimedEvents();
      resolve(true);
    });
  }

  _loadScreenSettings() {
    return new Promise(async resolve => {
      // load all the settings
      await this.settings.loadSettings(this.authentication.Active_bump_screen.id);

      // PRINTER SETUP
      // retrieves all saved printers
      await this.printer.loadSavedPrinters();

      // now create all the printer groups
      await this.printer.initialisePrinterGroups(this.authentication.Active_bump_screen.printing_groups);

      // activates the saved printer (IF AVAILABLE)
      let printerAddress = await this.storage.load(PrinterService.ACTIVE_PRINTER_STORAGE_KEY);
      if (printerAddress) {
        this.printer.activatePrinter(printerAddress);
      } else {
        // TRY THE OLD KEY ("printer")
        printerAddress = await this.storage.load('printer');

        // IF EXISTING - make sure this is added to the available printers
        if (printerAddress) {
          this.printer.activatePrinter(printerAddress);
        }
      }

      // REMOVE IT since we are migrating to a new cache key for multiple printers
      this.storage.remove(PrinterService.ACTIVE_PRINTER_STORAGE_KEY);
      resolve(true);
    });
  }

  /**
   * Opens a prompt to select a single bump screen from multiple items. A callback is required to pass
   * the selected screen.
   * @param screens - The collection of bump screen to select from
   * @param callback - The callback function called after selecting the screen
   */
  async handleMultipleBumpScreen(screens: Bump_screen[], callback: (screen: Bump_screen) => void) {
    const opts = {
      header: 'Order Management screen',
      cssClass: 'bump-screen-selector',
      backdropDismiss: false,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: data => {
            // welp, do nothing here
            if (callback) {
              callback(null);
            }
          },
        },
        {
          text: 'Continue',
          role: 'continue',
          handler: (data: Bump_screen) => {
            if (callback) {
              callback(data);
            }
          },
        },
      ],
      inputs: [],
    };

    each(screens, (screen: Bump_screen, index) => {
      opts['inputs'].push({
        name: 'screen' + index,
        type: 'radio',
        label: screen.title,
        value: screen,
      });
    });

    const alert = await this.alertCtrl.create(opts);
    this.alertQueue.queueAlert(alert, HubAlertModule.CHANGE_SCREEN);
  }

  /**
   * Allows changing of available bump screen.
   */
  async switchBumpScreen() {
    try {
      await this.toggleMainMenu(false, true);
    } catch (e) {
      // failed in toggling menu
    }

    this.handleMultipleBumpScreen(this.authentication.Bump_screens, async screen => {
      if (screen) {
        // make a copy to save it again
        const screens: Bump_screen[] = this.authentication.Bump_screens;

        // make a copy of the user id
        const userId: number = this.authentication.User_id;

        // don't forget to logout first
        await this.userLogout(false);

        // save the copies
        this.authentication.userLogin(userId);
        this.authentication.saveAllBumpScreens(screens);

        // now set your new screen
        this.setBumpScreen(screen);
      }
    });
  }

  /**
   * Opens the forgot password modal.
   * @param email - The email used to prefill the email form.
   */
  async forgotPassword(email: string) {
    const forgotPasswordModal = await this.modalCtrl.create({
      component: ForgotPasswordComponent,
      componentProps: {
        email: email,
      },
      cssClass: 'forgot-password-modal',
      backdropDismiss: false,
    });

    forgotPasswordModal.present();
  }

  /**
   * Logs out the user and clears the storage of any traces from the previous bump screen. Setting the
   * hard parameter to false will not redirect to login page but only clear variables and storage.
   *
   * @param hard - Flagger used to force the app to redirect to login page. Defaults to TRUE.
   */
  public userLogout(hard: boolean = true): Promise<any> {
    return new Promise(resolve =>
      // close the menu
      this.toggleMainMenu(false, true).then(async () => {
        // stop all timed events
        this.stopTimedEvents();

        // clear user
        await this.authentication.logout();

        // clear the preservice backlogs to print
        this._failedPrintedUpdateBacklog = [];

        // reset the services
        this.printer.reset();
        this.docketService.reset();
        this.vipService.reset();
        this.filterService.reset();
        this.messageBoard.reset();
        this.generatedDocketService.reset();
        this.docketLayoutService.reset();

        // remove the global key last
        this.storage.globalKey = '';

        if (hard) {
          this.disableBackgroundMode();
          document.body.classList.remove('dark');
          this.navigateTo(['/login']);
        }

        resolve(true);
      }),
    );
  }

  /** DOCKET FILTERS */

  /**
   * Sets which context filter to use
   * @param $event - Context filter to activate
   */
  contextTypeFilterChanged($event: IFilterObject): void {
    if ($event) {
      const contextFilters: IFilterObject[] = cloneDeep(this.filterService.Context_filters);
      each(contextFilters, (filter: IFilterObject) => {
        filter.toggled = $event.data == filter.data;
      });

      this.filterService.updateFiltersByGroup('context', contextFilters, true);

      // THE CODE BELOW SUPPORTS MULTIPLE FULFILLMENTS AT ONCE
      // const contextFilters: IFilterObject[] = cloneDeep(this.filterService.Context_filters);
      // if($event.data == 'all') {
      //   each(contextFilters, (filter: IFilterObject) => {
      //     filter.toggled = filter.data == "all";
      //   });
      // } else {

      //   const filterToToggle: IFilterObject = find(contextFilters, ['data', $event.data]);
      //   if(filterToToggle) {
      //     filterToToggle.toggled = !filterToToggle.toggled;

      //     if(filterToToggle.toggled) {
      //       // turn OFF all
      //       contextFilters[0].toggled = false;
      //     }
      //   }
      // }

      // // check make sure that there are toggled items
      // const toggledFilters: IFilterObject[] = filter(contextFilters, 'toggled');
      // if(size(toggledFilters) == 0) {
      //   // force set ALL to toggled
      //   contextFilters[0].toggled = true;
      // }

      // this.filterService.updateFiltersByGroup('context', contextFilters, true);
    }
  }

  /**
   * Sets which bump status filter to use
   * @param $event - Bump status filter to activate
   */
  async orderStatusFilterChanged($event: CustomEvent) {
    if ($event) {
      const bumpFilters: IFilterObject[] = cloneDeep(this.filterService.Bump_filters);
      each(bumpFilters, (filter: IFilterObject) => {
        filter.toggled = $event.detail.value == filter.data;
      });

      this.filterService.updateFiltersByGroup('bump', bumpFilters, true);
    }
  }

  /**
   * Sets the order date filter
   * @param $event - Order date filter to activate
   */
  orderDateFilterChanged($event: IFilterObject): void {
    if ($event) {
      const preorderFilters: IFilterObject[] = cloneDeep(this.filterService.Preorder_filters);
      each(preorderFilters, (filter: IFilterObject) => {
        filter.toggled = $event.data == filter.data;
      });

      this.filterService.updateFiltersByGroup('preorder', preorderFilters, true);
    }
  }

  /*** SIDE MENU */
  async sideMenuClicked(action: string, data?: any) {
    switch (action) {
      case 'AdminPin':
        if (this.authentication.Admin_pin) {
          const defaultEmail: string = this.authentication.Email;
          const loginModal = await this.modalCtrl.create({
            component: LoginModalComponent,
            cssClass: defaultEmail ? 'login-mini-modal' : 'login-default-modal',
            backdropDismiss: false,
            componentProps: {
              defaultEmail: defaultEmail,
            },
          });

          loginModal.onDidDismiss().then(result => {
            const { data } = result;
            if (data) {
              if (has(data, 'forgotPassword') && data.forgotPassword) {
                this.forgotPassword(this.authentication.Email);
              } else if (has(data, 'success') && data.success) {
                this.navigateTo(['/pin']);
              }
            }
          });

          await loginModal.present();
        } else {
          this.navigateTo(['/pin']);
        }
        break;
      case 'OrderRefund':
        let refundUrl: string = `${this.core.Active_server.url}account-orders/${data.id}/refund/${this.authentication.User_id}/ooHub/${this.authentication.Active_bump_screen.id}`;
        this.openUrl2(refundUrl + '?referrer=hub', '_blank');
        break;
      case 'AdminLink':
        await this.toggleMainMenu(false, true);

        //
        const pinException = data === 'out-of-stock';
        let validPin: boolean = false;

        if (!pinException) {
          validPin = await this.validatePinGateway();
        }

        if (validPin || pinException) {
          let strUrl = `${this.core.Active_server.url}${data}/${this.authentication.User_id}/ooHub`;
          if (data == 'account-orders') {
            strUrl = strUrl + '/' + this.authentication.Active_bump_screen.id;
          }

          this.openUrl2(strUrl + '?referrer=hub', '_blank');
          this.analytics.trackEvent('Admin URL', 'View: ' + strUrl, this.authentication.Tracking_key);
        }
        break;
      case 'ChangePassword':
        this.forgotPassword(this.authentication.Email);
        break;
      case 'VipSettings':
        await this.toggleMainMenu(false, true);
        const valid = await this.validatePinGateway();
        if (valid) {
          this.navigateTo(['/vip-settings']);
        }
        break;
      case 'SwitchScreen':
        this.switchBumpScreen();
        break;
      case 'Signout':
        await this.userLogout();
        break;
      case 'ChangeSettings':
        this.showAppSettings();
        break;
      case 'Walkthrough':
        if (this.walkthrough.getMaxSeries() > 0) {
          this.navigateTo(['walkthrough', 'start']);
        }
        break;
      case 'Support':
        this.analytics.showHubspot(resp => {
          if (!resp) this.openUrl2(this.config.support_url, '_blank');
        });

        // do not untoggle the menu
        return;
    }

    await this.toggleMainMenu(false, true);
  }

  /** BACKGROUND MODE */
  private _bgActivateSub: Subscription;
  public enableBackgroundMode(): void {
    if (!this.backgroundMode.isEnabled()) {
      // clear the SUB
      this.backgroundMode.setDefaults({
        title: 'HungryHungry Ready to Serve',
        text: 'Running in background to make sure we get your orders',
        icon: 'icon', // this will look for icon.png in platforms/android/res/drawable|mipmap
        color: 'E14365', // hex format like 'F14F4D'
        resume: true,
        hidden: true,
        bigText: false,
      });

      this._bgActivateSub = this.backgroundMode.on('activate').subscribe(() => {
        this.backgroundMode.disableWebViewOptimizations();
      });

      this.backgroundMode.setEnabled(true);
    } else {
      console.log('BG ALREADY ENABLED');
    }
  }

  public disableBackgroundMode(): void {
    if (this._bgActivateSub) this._bgActivateSub.unsubscribe();

    this.backgroundMode.setEnabled(false);
  }

  /**
   * Opens the oohq in an internal app browser
   * @param url - The target URL
   * @param target - The mode target to use. Defaults to _blank
   */
  async openUrl2(url: string, target: string = '_blank') {
    const browserOption: InAppBrowserOptions = {
      location: 'no',
      footer: 'yes',
      closebuttoncaption: '< Back to HungryHungry Ready to Serve',
      closebuttoncolor: '#444444',
      disallowoverscroll: 'yes',
      hardwareback: 'yes',
      shouldPauseOnSuspend: 'yes',
      fullscreen: 'yes',
      hidden: 'yes',
      hidenavigationbuttons: 'no',
      hideurlbar: 'yes',
      presentationstyle: 'formsheet',
      useWideViewPort: 'no',
      usewkwebview: 'yes',
      zoom: 'no',
      clearcache: this.authentication.New_user ? 'yes' : 'no',
      cleardata: this.authentication.New_user ? 'yes' : 'no',
      clearsessioncache: this.authentication.New_user ? 'yes' : 'no',
    };

    let inAppBrowserObj = this.inAppBrowser.create(url, target, browserOption);

    // create a loader
    const loading = await this.showAppLoading('Please wait...');

    const timeoutCall = setTimeout(async () => {
      if (inAppBrowserObj) {
        inAppBrowserObj.close();
        inAppBrowserObj = null;
      }

      if (loading) loading.dismiss();

      this.authentication.New_user = false;

      const alertMsg = await this.alertCtrl.create({
        header: "Sorry! We couldn't access that page",
        message: 'Please contact support if this problem persists',
        buttons: ['Dismiss'],
      });

      this.alertQueue.queueAlert(alertMsg, HubAlertModule.INAPPBROWSER_ERR);
    }, 60000);

    if (this.platform.is('cordova')) {
      await loading.present();
    }

    let stopSub;
    let errorSub;
    let exitSub;

    let onLoadStop;
    let onLoadError;
    let onExit;

    try {
      onLoadStop = inAppBrowserObj.on('loadstop');
      if (onLoadStop) {
        stopSub = onLoadStop.subscribe(evt => {
          loading.dismiss();
          inAppBrowserObj.show();
          this.authentication.New_user = false;
          clearTimeout(timeoutCall);

          const keepAwake = this.settings.getBumpScreenSettingByKey('general', 'keep_awake');
          if (keepAwake) {
            inAppBrowserObj.executeScript({
              code: `if(navigator['wakeLock']) {
                          navigator['wakeLock'].request('screen').then((ev) => {
                            console.log('wakeLock ready', ev);
                          });
                        }
                      `,
            });
          }
        });
      }

      onLoadError = inAppBrowserObj.on('loaderror');
      if (onLoadError) {
        errorSub = onLoadError.subscribe(evt => {
          loading.dismiss();
          inAppBrowserObj.show();
          this.authentication.New_user = false;
          clearTimeout(timeoutCall);

          const keepAwake = this.settings.getBumpScreenSettingByKey('general', 'keep_awake');
          if (keepAwake) {
            inAppBrowserObj.executeScript({
              code: `if(navigator['wakeLock']) {
                          navigator['wakeLock'].request('screen').then((ev) => {
                            console.log('wakeLock ready', ev);
                          });
                        }
                      `,
            });
          }
        });
      }

      onExit = inAppBrowserObj.on('exit');
      if (onExit) {
        exitSub = onExit.subscribe(event => {
          if (inAppBrowserObj) {
            inAppBrowserObj.close();
            inAppBrowserObj = null;
          }

          if (stopSub) stopSub.unsubscribe();
          if (exitSub) exitSub.unsubscribe();
          if (errorSub) errorSub.unsubscribe();

          onLoadError = null;
          onLoadStop = null;
          onExit = null;
        });
      }
    } catch (e) {
      if (inAppBrowserObj) {
        inAppBrowserObj.close();
        inAppBrowserObj = null;
      }

      if (stopSub) stopSub.unsubscribe();
      if (exitSub) exitSub.unsubscribe();
      if (errorSub) errorSub.unsubscribe();

      onLoadError = null;
      onLoadStop = null;
      onExit = null;

      if (loading) loading.dismiss();

      clearTimeout(timeoutCall);
    }
  }

  public printRetry(order: Order): Promise<IPrintResponse> {
    return new Promise(async retryPrintDocketResolver => {
      if (order) {
        const updatedOrder: Order = this.docketService.getOrderById(order.id);
        const retryResult: IPrintResponse = await this.printDocket(updatedOrder, PRINT_TRIGGER.PRINT_RETRY);
        if (retryResult.success && updatedOrder.auto_set_eta && !updatedOrder.printed) {
          const success = await this.dispatchPrintedService.updateDocketPrintStatus([updatedOrder.id], true, false);
          if (!success) {
            this.analytics.trackEvent(
              'Auto Set ETA',
              'Print retry attempt failed (set printed): ' + updatedOrder.id,
              this.authentication.Tracking_key,
            );
          }
        } else {
          this.analytics.trackEvent(
            'Auto Set ETA',
            'Print retry attempt failed (printing): ' + updatedOrder.id,
            this.authentication.Tracking_key,
          );
        }

        retryPrintDocketResolver(retryResult);
      } else {
        retryPrintDocketResolver({
          success: false,
          message: "Order doesn't exist",
        });
      }
    });
  }

  /**
   * Prints the receipt of the docket
   * @param order - The order that contains the receipt data to print
   * @param trigger - The trigger that invoked the printing of docket
   * @returns Promise to notify printing process is done. It will resolve an object to return the success status of the process.
   */
  public printDocket(order: Order, trigger: string): Promise<IPrintResponse> {
    return new Promise(async printDocketResolver => {
      if (!order) {
        this.analytics.trackEvent(
          'Print Result V2',
          'Trying to print an empty order',
          this.authentication.Tracking_key,
        );

        printDocketResolver({
          success: true,
          message: '',
          data: {
            completion: 'full',
            failed: [],
          },
        });
        return;
      }

      // only print if the order has an ETA
      if (order.eta) {
        // override result with SUCCESS on DEV MODE
        if (this.core.DEV_MODE && !this.platform.is('cordova')) {
          setTimeout(() => {
            printDocketResolver({
              success: true,
              message: '',
              data: {
                completion: 'full',
                failed: [],
              },
            });
          }, 500);
          return;
        }

        // no connected printers
        if (size(this.printer.Available_printers) == 0) {
          this._interceptPrintResp([{ success: false, message: 'No connected printer' }], [], {
            id: order.id,
            trigger: trigger,
            service: 'unknown',
          });

          printDocketResolver({ success: false, message: 'No connected printers' });
          return;
        }

        // no active printers
        const activePrinters: Printer[] = this.printer.getActivePrinters();
        if (size(activePrinters) == 0) {
          this.promptFailedPrinting(
            order,
            `There are <span class='emp'>no active printers</span> to print the dockets for <span class='emp'>${order.docket_display_label}</span>.
                                      <br><span class="printer-status-message">The printers can be activated in the <strong class="emp">Printer Setup</strong>.</span>`,
            null,
          );

          this._interceptPrintResp([{ success: false, message: 'No active printers' }], [], {
            id: order.id,
            trigger: trigger,
            service: 'unknown',
          });

          printDocketResolver({ success: false, message: 'No active printers' });
          return;
        }

        // Collection of print job requests - this is per printer
        const printJobRequest: Promise<IPrintResponse>[] = [];

        // Collection of printer
        let printJobsPerPrinter: IPrinterJobQueue[] = [];

        // if retry, only print the ones that failed previously
        let targetPrinters: string[] = [];
        if (trigger == PRINT_TRIGGER.PRINT_RETRY) {
          each(order.Printer_errors, (err: IPrintResponse) => {
            if (err.data && err.data instanceof Printer) {
              targetPrinters.push(err.data.Address);
            } else if (err.extra && err.extra.printer) {
              targetPrinters.push(err.extra.printer.address);
            }
          });

          // show the retrying message
          this.messageBoard.retryingOrder();
        }

        // Insert a print job queue or append if one already exist
        const queueDocket = (address: string, docketToPrint: IPrintStringObj, printerGroup: PrinterGroup | string) => {
          const printerJob: any = find(printJobsPerPrinter, job => job.printer_address == address);
          if (printerJob) {
            printerJob.printer_groups.push(
              printerGroup && printerGroup instanceof PrinterGroup ? `${printerGroup.ID}:${printerGroup.Name}` : 'Full',
            );
            printerJob.formatted_dockets.push(docketToPrint.data);
          } else {
            printJobsPerPrinter.push({
              printer_address: address,
              printer_groups: [
                printerGroup && printerGroup instanceof PrinterGroup
                  ? `${printerGroup.ID}:${printerGroup.Name}`
                  : 'Full',
              ],
              formatted_dockets: [docketToPrint.data],
            });
          }
        };

        // format generated docket here
        let formattedDocket: IFormattedDocket;
        if (
          this.featureFlagService.isFeatureFlagEnabled(FeatureFlagList.BFF) &&
          this.featureFlagService.isFeatureFlagEnabled(FeatureFlagList.DOCKET_LAYOUT)
        ) {
          if (size(this.docketLayoutService.Docket_layout_config)) {
            const generatedDocket = this.generatedDocketService.getGeneratedDocketById(order.id);

            if (generatedDocket) formattedDocket = this.docketLayoutService.format(generatedDocket);
            else
              this.analytics.trackEvent(
                'Print Layout Config',
                `Missing Generated Docket: Printing #${order.id} default`,
                this.authentication.Tracking_key,
              );
          } else {
            this.analytics.trackEvent(
              'Print Layout Config',
              `Missing Config: Printing #${order.id} default`,
              this.authentication.Tracking_key,
            );
          }
        }

        const passPrinters: Printer[] = this.printer.getPassPrinters();
        each(passPrinters, (passPrinter: Printer) => {
          if (size(targetPrinters) == 0 || targetPrinters.indexOf(passPrinter.Address) > -1) {
            const receiptSrc: string[] =
              formattedDocket && formattedDocket.receipt ? formattedDocket.receipt : order.receipt;
            const passDocketToPrint: IPrintStringObj = this.printer.formatDocketToPrint(
              receiptSrc,
              passPrinter.Print_repeat,
            );
            queueDocket(passPrinter.Address, passDocketToPrint, 'Full');
          }
        });

        // create jobs for each grouped dockets
        const splitDocketSrc = formattedDocket ? formattedDocket.split_dockets : order.split_dockets;

        forOwn(splitDocketSrc, (docket: string[], printerGroupId: number) => {
          // check if printer group is available
          const printerGroup: PrinterGroup = this.printer.getPrinterGroupById(printerGroupId.toString());
          if (printerGroup && size(printerGroup.Linked_printers)) {
            each(printerGroup.Linked_printers, (address: string) => {
              if (size(targetPrinters) == 0 || targetPrinters.indexOf(address) > -1) {
                const printerToUse: Printer = this.printer.getPrinterByAddress(address);
                if (printerToUse) {
                  const splitDocketToPrint: IPrintStringObj = this.printer.formatDocketToPrint(
                    docket,
                    printerToUse ? printerToUse.Print_repeat : 1,
                  );

                  // let it continue even if printer doesn't exist - let the printer service create the fail response
                  queueDocket(address, splitDocketToPrint, printerGroup);
                }
              }
            });
          }
        });

        // Bundle printing jobs PER PRINTER
        each(printJobsPerPrinter, printJob => {
          printJobRequest.push(
            this.printer.printReceiptByAddress(
              printJob.printer_address,
              {
                data: printJob.formatted_dockets.join(''),
              },
              false,
            ),
          );
        });

        // NO PRINTER
        // OR NOTHING TO PRINT
        if (size(printJobRequest) == 0) {
          if (this.printer.showNoDocketErrorMessage === 'ON') {
            this.promptFailedPrinting(
              order,
              `There were no dockets created for <span class='emp'>${order.docket_display_label}</span>.<br><span class="printer-status-message">The printers can be configured in the <strong class="emp">Printer Setup</strong>.</span>`,
              null,
            );
          }

          this._interceptPrintResp([{ success: false, message: 'No print jobs created' }], [], {
            id: order.id,
            trigger: trigger,
            service: formattedDocket ? 'bff' : 'drupal',
          });

          // adjust retry count - reduce the number of retries
          if (trigger == PRINT_TRIGGER.PRINT_RETRY) {
            this.messageBoard.retryOrderComplete();
          }

          printDocketResolver({ success: false, message: 'No print jobs created' });
          return;
        }

        Promise.all(printJobRequest).then((printResponses: IPrintResponse[]) => {
          // show error message
          const fails: IPrintResponse[] = filter(printResponses, (resp: IPrintResponse) => {
            return !resp.success;
          });

          // now update the order
          order.updatePrinterErrors(fails);

          if (size(fails) > 0) {
            this.promptFailedPrinting(order, null, fails);
          } else {
            /** Remove all printing error message boards of the order */
            let relatedMessageBoardItems: MessageBoardItem[] = this.messageBoard.findAllItemsByDetailsKeyValuePair(
              'order',
              order.id,
              MESSAGE_BOARD_ITEM_TYPE.PRINTER_ERROR,
            );
            each(relatedMessageBoardItems, item => this.messageBoard.removeItem(item.ID));
          }

          // FOR LOGGING
          if (formattedDocket) {
            // BFF
            console.log('FORMATTED DOCKET BFF', formattedDocket);
          } else {
            // DRUPAL
            console.log('FORMATTED DOCKET DRUPAL', {
              receipt: order.receipt,
              split_dockets: order.split_dockets,
            });
          }

          // intercept for analytics
          this._interceptPrintResp(printResponses, printJobsPerPrinter, {
            id: order.id,
            trigger: trigger,
            service: formattedDocket ? 'bff' : 'drupal',
          });

          // adjust retry count
          if (trigger == PRINT_TRIGGER.PRINT_RETRY) {
            this.messageBoard.retryOrderComplete();
          }

          // resolve printing result
          const failCnt: number = size(fails);
          const printJobsCnt: number = size(printJobRequest);

          printDocketResolver({
            success: failCnt == 0,
            message: '',
            data: {
              completion: failCnt == 0 ? 'full' : failCnt < printJobsCnt ? 'partial' : 'none',
              failed: fails,
            },
          });
        });
      } else {
        let message: string = `<p>Sorry! You cannot print the docket without setting the <span class="emp">${order.context_title}</span> ETA.</p><br>Please set the <span class="emp">${order.context_title}</span> ETA.`;
        let okBtn: string;

        if (order.context_type == CONTEXT_TYPE.Dine_in) {
          message =
            '<p>Sorry! You cannot print the docket without confirming the order. Please confirm the order to continue.</p>';
          okBtn = 'Confirm Order';
        }

        let alert = await this.alertCtrl.create({
          header: 'Failed to Print!',
          message: message,
          cssClass: 'printer-errMsg',
          buttons: [
            {
              text: 'Dismiss',
            },
            {
              text: okBtn,
              handler: () => {
                this.setETASelectorHandler(order, true);
              },
            },
          ],
        });

        this.alertQueue.queueAlert(alert, HubAlertModule.NO_ETA_ERR);
        printDocketResolver({ success: false, message: 'NO ETA' });
      }
    });
  }

  /**
   * Generates the printing error message and prompt the user.
   * This will also create the message board if an order is provided
   *
   * @param order The order to print
   * @param errMsg The error message to display
   * @param fails The list of all failed dockets to print and reason
   */
  promptFailedPrinting(order: Order, errMsg: string, fails: IPrintResponse[]) {
    if (!errMsg && (!fails || fails.length == 0)) {
      // must have an Error Message OR list of failed responses
      return;
    }

    /** Composes the summary of error messages to display */
    let alertMessage: string = '';
    if (size(fails) > 0) {
      each(fails, (fail: IPrintResponse, index: number) => {
        alertMessage += `Order failed to print at <span class='emp'>${
          fail.extra['printer']['name']
        }</span><br><span class="printer-status-message">${Printer.GetErrorMessageByStatus(fail.message, false)}`;
        if (index != fails.length - 1) {
          alertMessage += '<br><br><br>';
        }

        alertMessage += '</span>';
      });
    } else {
      alertMessage = errMsg;
    }

    this.createFailedPrintingAlert(`<br>${alertMessage}`);

    /** Update the message board */
    if (order) {
      let messageBoardMessage: string = '';
      if (fails && fails.length > 0) {
        const printerNames: string[] = map(fails, (fail: IPrintResponse) => {
          if (fail.data && fail.data instanceof Printer) {
            return `<span class='emp'>${fail.data.Name}</span>`;
          } else if (fail.extra && fail.extra.printer) {
            return `<span class='emp'>${fail.extra.printer.name}</span>`;
          }
          return '';
        });

        let combinedNames: string;
        if (printerNames && printerNames.length > 0) {
          if (printerNames.length > 2) {
            combinedNames = printerNames.join(', ');

            const lastIndexPosition: number = combinedNames.lastIndexOf(', ');
            combinedNames =
              combinedNames.substr(0, lastIndexPosition) + ' & ' + combinedNames.substr(lastIndexPosition + 2);
          } else if (printerNames.length == 2) {
            combinedNames = printerNames.join(' & ');
          } else {
            combinedNames = printerNames[0];
          }

          messageBoardMessage = `${combinedNames} docket failed to print for <span class='emp'>${order.docket_display_label}</span>`;
        }
      } else {
        messageBoardMessage = errMsg;
      }

      this.createFailedPrintingMessageBoard(order, messageBoardMessage);
    }
  }

  /**
   * Generate the message board item
   * @param order - The order connected to message board
   * @param message - The message to display
   */
  createFailedPrintingMessageBoard(order: Order, message: string) {
    // check if the message board exist
    const messageBoardConfig: IMessageBoardConfig = {
      message: message,
      details: {
        order: order.id,
        context_type: order.context_type,
      },
      type: MESSAGE_BOARD_ITEM_TYPE.PRINTER_ERROR,
    };

    let relatedMessageBoardItem: MessageBoardItem = this.messageBoard.findItemByDetailsKeyValuePair(
      'order',
      order.id,
      MESSAGE_BOARD_ITEM_TYPE.PRINTER_ERROR,
    );
    if (relatedMessageBoardItem) {
      relatedMessageBoardItem.updateItem(messageBoardConfig);
    } else {
      relatedMessageBoardItem = new MessageBoardItem(messageBoardConfig);
    }

    // always add item even if overwrite is TRUE, this is to update the time created time
    this.messageBoard.addItem(relatedMessageBoardItem, true);
  }

  /**
   * Prompts an alert to the user about the failed printing
   * @param err - The error message
   * @param retry - Flagger to allow the user to trigger a retry. Defaults to TRUE.
   */
  async createFailedPrintingAlert(err: string, retry: boolean = true) {
    const buttons: any[] = [
      {
        text: 'Dismiss',
        handler: () => {
          // clear all pending alerts
          this.alertQueue.clearByModule(HubAlertModule.PRINT_ERR);
        },
      },
      {
        text: 'More info',
        cssClass: 'print-errMsg-more-info',
        handler: () => {
          this.messageBoard.show();
        },
      },
    ];

    if (retry) {
      buttons.push({
        text: 'Retry',
        handler: () => {
          // clear all pending alerts
          this.alertQueue.clearByModule(HubAlertModule.PRINT_ERR);

          // attempt to retry ALL queued failed printing
          this.messageBoard.retryAllFailedPrintingError();
        },
      });
    }

    const config = {
      backdropDismiss: true,
      header: 'Failed to Print!',
      message: err,
      buttons: buttons,
      cssClass: 'printer-errMsg',
    };

    await this.alertQueue.createAndQueue(config, HubAlertModule.PRINT_ERR, false, true);
  }

  /**
   * Collects data from printing and track the event to analytics
   * @param responses The list of all print job responses
   * @param printerJobQueue The list of all print jobs
   * @param extras Additional data to track
   */
  private _interceptPrintResp(
    responses: IPrintResponse[],
    printerJobQueue: IPrinterJobQueue[],
    extras: { id: string; trigger: string; service: string },
  ): void {
    let order: Order;
    if (extras && extras.id) {
      order = this.docketService.getOrderById(extras.id);
    }

    const fails: IPrintResponse[] = filter(responses, (resp: IPrintResponse) => {
      return !resp.success;
    });

    let summary: string[] = [];
    each(responses, (response: IPrintResponse) => {
      if (response.extra && response.extra.printer) {
        const printerJob: IPrinterJobQueue = find(
          printerJobQueue,
          (job: IPrinterJobQueue) => job.printer_address == response.extra.printer.address,
        );
        if (printerJob) {
          let printerSummary: string = `G(${printerJob.printer_groups.join(',')}), P(${
            response.extra.printer.address
          }), (R:${response.message})`;
          summary.push(`${response.success ? 'SUCCESS' : 'FAILED'} - ${printerSummary}`);
        } else {
          // can't find the print job - eh what?
          summary.push(`FAILED - R:${response.message}`);
        }
      } else {
        summary.push(`FAILED - R:${response.message}`);
      }
    });

    const trackData = {
      success: !fails || fails.length == 0,
      completion: fails.length == 0 ? 'full' : fails.length < responses.length ? 'partial' : 'none',
      summary: summary,
      order_id: extras ? extras.id : '',
      trigger: extras ? extras.trigger : '',
      auto_set: order ? order.auto_set_eta : '',
      printed: order ? order.printed : '',
      update_time: order ? order.last_updated_time : '',
      service: extras ? extras.service : '',
    };

    console.log(`PRINT RESPONSE`, trackData);

    this.analytics.trackEvent('Print Result V2', JSON.stringify(trackData), this.authentication.Tracking_key);
  }

  /**
   * Opens the settings modal
   */
  async showAppSettings(): Promise<any> {
    const appSettings = await this.modalCtrl.create({
      component: AppSettingsComponent,
      cssClass: 'app-settings-modal',
      backdropDismiss: false,
    });

    appSettings.onDidDismiss().then(value => {
      const { data } = value;
      if (data && data.support) {
        setTimeout(() => {
          this.openUrl2(this.config.support_url, '_blank');
        }, 300);
      }
    });

    return await appSettings.present();
  }

  /**
   * Shows the app loading spinner
   * @param message - The message to append on the loading spinner
   */
  async showAppLoading(message): Promise<any> {
    const loading = await this.loadingCtrl.create({
      spinner: 'crescent',
      message: message,
      backdropDismiss: false,
    });

    return loading;
  }

  /**
   * Opens an order additional details modal
   * @param order - The order to view
   */
  async showOrderInfo(order: Order) {
    const orderInfo = await this.modalCtrl.create({
      component: InfoComponent,
      cssClass: 'info-modal',
      componentProps: {
        order: order,
      },
    });

    orderInfo.onWillDismiss().then(async response => {
      let { data } = response;
      if (data && data.print) {
        await this.printDocket(order, PRINT_TRIGGER.INFO);
      }

      if (data && data.eta) {
        // add a delay so it doesn't overlap
        setTimeout(() => {
          this.setETASelectorHandler(order, true);
        }, 300);
      }
    });

    return await orderInfo.present();
  }

  /**
   * Opens a modal to set the order ETA and then send to the server to update
   *
   * Specifically for Non Dine IN orders or any orders that requires modal eta prompt.
   *
   * @param order - The order to set the ETA with
   * @param print - Flagger to print the order after setting the order
   */
  async setOrderETA(order: Order, print: boolean): Promise<boolean> {
    return new Promise(async resolve => {
      if (this._etaModalOpen) {
        resolve(false);
        return;
      }

      this._etaModalOpen = true;
      const etaModal = await this.modalCtrl.create({
        component: EtaSelectorComponent,
        componentProps: {
          order: order,
        },
        backdropDismiss: true,
        cssClass: 'eta-selector-modal',
      });

      if (etaModal) {
        etaModal.onDidDismiss().then(async data => {
          this._etaModalOpen = false;
          resolve(true);

          if (data && data.data && data.data.eta) {
            const success = await this.dispatchEtaService.updateDocketEta(
              moment(data.data.eta).format('YYYY-MM-DDTHH:mm:ss'),
              order.id,
            );
            if (print && success) {
              // get the new docket with updated RECEIPT
              const docketToPrint: Order = this.docketService.getOrderById(order.id);
              await this.printDocket(docketToPrint, PRINT_TRIGGER.ETA_SELECTOR);
            }
          }
        });

        await etaModal.present();
      } else {
        resolve(false);
      }
    });
  }

  /**
   * Handler function to check how the user will be prompted for the ETA request. Dine in
   * will be auto set while the rest will be prompted with a modal.
   *
   * @param docket - The order to set the ETA with
   * @param print - Flagger to print the order after setting the order
   * @param handleError - Flagger to show an error message on fail
   */
  async setETASelectorHandler(docket: Order, print: boolean, handleError: boolean = true): Promise<boolean> {
    return new Promise(async resolve => {
      if (!docket.eta) {
        if (docket.context_type == CONTEXT_TYPE.Dine_in) {
          // IF DINEIN - Auto set the ETA
          // simulate an eta selector
          const eta_time: string = this.core.Server_time.format('YYYY-MM-DDTHH:mm:ss');
          const success = await this.dispatchEtaService.updateDocketEta(eta_time, docket.id, handleError);
          if (print && success) {
            // get the new docket with updated RECEIPT
            const docketToPrint: Order = this.docketService.getOrderById(docket.id);
            await this.printDocket(docketToPrint, PRINT_TRIGGER.ETA_SELECTOR);
          }

          resolve(true);
        } else {
          // ASK FOR THE ETA MODAL FOR THE REST
          let resp = await this.setOrderETA(docket, print);
          resolve(resp);
        }
      } else {
        resolve(true);
      }
    });
  }

  /**
   * Update the orders bump status to BUMPED
   * @param order - The order to update
   */
  async setBumpOrder(order: Order) {
    if (!order) {
      // fallback
      return;
    }

    if (order.eta) {
      // update the bump status
      const success = await this.dispatchBumpService.updateDocketBumpStatus(order.id, 'bumped');

      // then send the SMS
      if (
        success &&
        order.is_collect_at_counter &&
        !order.collect_at_counter_sms &&
        !order.isFoodDrinkSplitEnabled(this.authentication.Active_bump_screen)
      ) {
        const send_time: string = this.core.Server_time.format('YYYY-MM-DDTHH:mm:ss');
        this.dispatchSmsService.sendDocketSMS(order.id, send_time);
      }

      // force a list update
      this.updateDisplayedOrderItemsAndCount();
    } else {
      let title: string = 'Oops! Something went wrong...';
      let message: string = `Sorry! You cannot bump an order without setting the ${order.context_title} ETA. Please set the ETA and try again.`;
      let okBtn: string;
      if (order.context_type == CONTEXT_TYPE.Dine_in) {
        message = 'Sorry! You cannot bump an order without confirming. Please confirm the order to continue.';
        okBtn = 'Confirm Order';
      }

      let alertCtrl = await this.alertCtrl.create({
        header: title,
        message: message,
        buttons: [
          {
            text: okBtn || 'Continue',
            handler: () => {
              this.setETASelectorHandler(order, true);
            },
          },
        ],
      });

      this.alertQueue.queueAlert(alertCtrl, HubAlertModule.NO_ETA_ERR);
    }
  }

  /**
   * Sends the food or drink SMS to the customer
   * @param order - The order to send the SMS to
   * @param type  - The type of SMS to send (food or drink)
   */
  async sendFoodDrinkSMS(order: Order, type: FoodDrinkType) {
    // Use current time instead of Server_time (which only updates every minute)
    const send_time: string = UtilityClass.momentNow(this.core.Timezone).format('YYYY-MM-DDTHH:mm:ss');
    console.log('send_time:', send_time);
    this.dispatchSmsService.sendFoodOrDrinkSMS(order.id, send_time, type);
  }

  /**
   * Update the orders bump status to NEW
   * @param order - The order to update
   */
  async setUnbumpOrder(order: Order) {
    this.dispatchBumpService.updateDocketBumpStatus(order.id, 'new');

    // force a list update
    this.watcher.broadcast(APP_DOCKET_DISPLAY_UPDATE, 'setUnbumpOrder');
  }

  /**
   * Prompts the user if there are any future day preorders to print.
   * Batch orders are not included here as future day is not feasible for Dine In orders.
   */
  async checkFutureDayPreOrdersToPrint() {
    const lastPreorderPrompt: any = await this.storage.load('preorder_prompt', false);
    if (lastPreorderPrompt) {
      const lastPreorderPromptMoment: moment.Moment = moment(lastPreorderPrompt, 'YYYY-MM-DD', this.core.Timezone);
      if (lastPreorderPromptMoment.isSameOrAfter(this.core.Server_time, 'day')) {
        // don't bother asking if already asked for today
        return;
      }
    }

    // if the modal is already open - don't ask
    // if there's no connected printer - don't ask
    if (this._futureOrderPrompt) return;

    const ordersToPrint: Order[] = this.docketService.findAllFutureOrdersForToday();
    const orderCount: number = size(ordersToPrint);
    if (orderCount > 0) {
      this._futureOrderPrompt = await this.modalCtrl.create({
        component: PreserviceAlertComponent,
        showBackdrop: true,
        cssClass: 'preservice-alert-modal',
      });

      this._futureOrderPrompt.onWillDismiss().then(async response => {
        // remove the modal
        this._futureOrderPrompt = null;

        // make sure you flag that you already asked for today
        await this.storage.save('preorder_prompt', this.core.Server_time.format('YYYY-MM-DD'), 0, false);

        // grab the response
        let { data } = response;
        if (size(ordersToPrint)) {
          if (data && data.print) {
            // make sure to get the data again
            if (size(ordersToPrint)) {
              this.printAutoSetETAOrders(ordersToPrint, true, PRINT_TRIGGER.PREORDER_PROMPT);
            }
          } else {
            let successfulSingleOrders: string[] = map(ordersToPrint, (order: Order) => order.id);
            const success = await this.dispatchPrintedService.updateDocketPrintStatus(
              successfulSingleOrders,
              true,
              false,
            );
            if (!success) {
              this._failedPrintedUpdateBacklog.push({ ids: successfulSingleOrders });
            }
          }
        }
      });

      this._futureOrderPrompt.present();
    }
  }

  /**
   * Checks if there are pre-service orders today with auto ETA that has not been printed yet
   * This will include delivery windows ordered on the same day
   */
  async checkAutoSetETAOrdersForToday() {
    if (!this.authentication.Logged_in) return;

    const order: Order = this.docketService.getPendingAutoSetETAOrderToPrint();
    if (size(order)) {
      console.log('ATTEMPTING TO AUTO PRINT via [checkAutoSetETAOrdersForToday]', order.id);
      this.docketService.removePendingAutoSetETAOrderToPrint(order.id);

      this.analytics.trackEvent('Auto Set ETA Print', 'Start:' + order.id, this.authentication.Tracking_key);
      // ignore the ID once auto printing is attempted
      // will retry on next poll completed
      const printed = await this.printAutoSetETAOrder(order, PRINT_TRIGGER.AUTO_SET_ETA);

      // if failed, make the printed flag to FALSE again
      if (!printed) {
        console.log('RESETTING PRINTED STATUS TO FALSE via [checkAutoSetETAOrdersForToday]', order.id);
        this.docketService.removePendingAutoSetETAOrderToPrint(order.id, false, true);
        this.analytics.trackEvent('Auto Set ETA Print', 'Failed:' + order.id, this.authentication.Tracking_key, true);
      } else {
        console.log('AUTO PRINT ATTEMPT COMPLETE via [checkAutoSetETAOrdersForToday]', order.id);
        this.analytics.trackEvent('Auto Set ETA Print', 'Success:' + order.id, this.authentication.Tracking_key, true);
      }

      // for auto set eta, just check the next order right away
      this.checkAutoSetETAOrdersForToday();
    }
  }

  async printAutoSetETAOrder(order: Order, trigger: string): Promise<boolean> {
    return new Promise(async resolve => {
      const allowByDevMode: boolean = this.core.DEV_MODE && !this.platform.is('cordova');
      if (!allowByDevMode) {
        // intercept early to prevent setting the ETA
        if (size(this.printer.Available_printers) == 0) {
          this.analytics.trackEvent(
            'Auto Set ETA Print',
            'No available printers:' + order.id,
            this.authentication.Tracking_key,
            true,
          );
          resolve(false);
          return;
        }

        // check if there is a printer
        if (!this.printer.Has_active_printers) {
          const alertMessage: string = `There are <span class='emp'>no active printers</span> to print the dockets for <span class='emp'>${order.docket_display_label}</span>.
                                        <br><span class="printer-status-message">The printers can be activated in the <strong class="emp">Printer Setup</strong>.</span>`;
          this.promptFailedPrinting(order, alertMessage, []);

          this.analytics.trackEvent(
            'Auto Set ETA Print',
            'No active printer:' + order.id,
            this.authentication.Tracking_key,
            true,
          );
          resolve(false);
          return;
        }
      }

      // grab the most latest order to make sure we have the most udpated receipt
      const docketToPrint: Order = this.docketService.getOrderById(order.id);
      const printResp: IPrintResponse = await this.printDocket(docketToPrint, trigger);

      // check the result, if successful - then update the printed status of the order
      if (printResp.success || (printResp.data && printResp.data.completion == 'partial')) {
        // Update order printed status to TRUE
        const printedSuccessRep = await this.dispatchPrintedService.updateDocketPrintStatus([order.id], true);
        console.log('RESULT ORDER PRINTED:', order.id, printedSuccessRep);

        if (!printedSuccessRep) {
          let exist: boolean = false;
          each(this._failedPrintedUpdateBacklog, ({ ids }) => {
            const orderIndex: number = findIndex(ids, id => id === order.id);
            if (orderIndex > -1) {
              exist = true;
              return false;
            }
          });

          if (!exist) {
            this._failedPrintedUpdateBacklog.push({ ids: [order.id] });
            console.log('ADDING ORDER BACKLOG:', order.id, this._failedPrintedUpdateBacklog);
          } else {
            console.log('SKIPPING ORDER BACKLOG:', order.id);
          }
        } else {
          console.log('PRINTED ORDER UPDATED:', order.id);
        }

        // SUCCESS - ORDER PRINTED
        resolve(true);
      } else {
        this.analytics.trackEvent(
          'Auto Set ETA Print',
          'Failed to print:' + order.id + ' Message:' + printResp.message,
          this.authentication.Tracking_key,
          true,
        );
        resolve(false);
      }
    });
  }

  /**
   * Auto prints orders with AUTO_SET_ETA = true and update their printed value to TRUE if successful
   * @param orders - The order items to print and update
   * @param showError - Flagger to show the error
   */
  printAutoSetETAOrders(orders: Order[], showError: boolean, trigger: string) {
    let successfulSingleOrders: string[] = [];
    let unsuccessfulSingleOrders: string[] = [];
    let index: number = 0;

    const maxCount: number = size(orders);
    const printOrder: Subject<Order> = new Subject();
    const completePrinting: Subject<boolean> = new Subject();

    // move to the next order
    const nextOrderOrFinish = () => {
      index++;
      if (index < maxCount) {
        printOrder.next(orders[index]);
      } else {
        if (printSub) printSub.unsubscribe();
        completePrinting.next(true);
      }
    };

    const printSub: Subscription = printOrder.subscribe(async (order: Order) => {
      console.log('ATTEMPTING TO AUTO PRINT:', order.id, trigger);

      // print the docket
      const docketToPrint: Order = this.docketService.getOrderById(order.id);
      const printResp: IPrintResponse = await this.printDocket(docketToPrint, trigger);
      console.log('PRINT RESP:', order.id, printResp);

      if (printResp.success || (printResp.data && printResp.data.completion == 'partial')) {
        // Update order printed status to TRUE
        console.log('UPDATING ORDER PRINTED:', order.id);
        const printedSuccessRep = await this.dispatchPrintedService.updateDocketPrintStatus([order.id], true);
        console.log('RESULT ORDER PRINTED:', order.id, printedSuccessRep);

        if (!printedSuccessRep) {
          let exist: boolean = false;
          each(this._failedPrintedUpdateBacklog, ({ ids }) => {
            const orderIndex: number = findIndex(ids, id => id === order.id);
            if (orderIndex > -1) {
              exist = true;
              return false;
            }
          });

          if (!exist) {
            this._failedPrintedUpdateBacklog.push({ ids: [order.id] });
            console.log('ADDING ORDER BACKLOG:', order.id, this._failedPrintedUpdateBacklog);
          } else {
            console.log('SKIPPING ORDER BACKLOG:', order.id);
          }
        } else {
          console.log('PRINTED ORDER UPDATED:', order.id);
        }
      } else {
        unsuccessfulSingleOrders.push(order.id);
      }

      nextOrderOrFinish();
    });

    const completePrintSub: Subscription = completePrinting.subscribe(async done => {
      if (completePrintSub) completePrintSub.unsubscribe();
      console.log('SUCCESSFUL SINGLE ORDERS', successfulSingleOrders);
      console.log('UNSUCCESSFUL SINGLE ORDERS', unsuccessfulSingleOrders);

      if (size(successfulSingleOrders) > 0) {
        const success = await this.dispatchPrintedService.updateDocketPrintStatus(successfulSingleOrders, true);
        if (!success) {
          this._failedPrintedUpdateBacklog.push({ ids: successfulSingleOrders });
          this.analytics.trackEvent(
            'Auto Set ETA',
            'Orders printed but failed to update: ' + successfulSingleOrders.join(','),
            this.authentication.Tracking_key,
          );
        }
      }

      if (size(unsuccessfulSingleOrders) > 0) {
        if (showError) {
          const orders: string = unsuccessfulSingleOrders.join('<br/>');
          this.alertQueue.createAndQueue(
            {
              header: 'Oops! Something went wrong...',
              message: `There are ${unsuccessfulSingleOrders.length} order(s) that failed to print. <br/><br/> ${orders}`,
              buttons: ['Dismiss'],
              backdropDismiss: true,
            },
            HubAlertModule.PRINT_PRESERVICE_ORDERS,
            true,
            true,
          );
        }

        this.analytics.trackEvent(
          'Auto Set ETA',
          'Failed to print orders: ' + unsuccessfulSingleOrders.join(','),
          this.authentication.Tracking_key,
        );
      }

      // TRIGGER AN UPDATE ON ORDER LIST
      this.updateDisplayedOrderItemsAndCount();
    });

    // start the loop
    printOrder.next(orders[index++]);
  }

  /**
   * Clears any preservice backlog - backlogs are failed printed status update
   */
  async clearAutoSetETAPrintBacklog() {
    // do a preservice backlog check
    if (size(this._failedPrintedUpdateBacklog)) {
      try {
        const _cloneBacklog: { ids: string[] }[] = this._failedPrintedUpdateBacklog.splice(0);
        this._failedPrintedUpdateBacklog = [];
        for (let i: number = size(_cloneBacklog); i != 0; i--) {
          const backlog: { ids: string[] } = _cloneBacklog.pop();
          const success = await this.dispatchPrintedService.updateDocketPrintStatus(backlog.ids, true, false);
          if (!success) {
            this._failedPrintedUpdateBacklog.push(backlog);
            this.analytics.trackEvent(
              'Auto Set ETA',
              'Backlog attempt failed: ' + backlog.ids.join(','),
              this.authentication.Tracking_key,
            );
          }
        }
      } catch (e) {
        // if there's an error, fail silently and log
        this.analytics.trackEvent('Auto Set ETA', 'Error in clearing print backlog', this.authentication.Tracking_key);
      }
    }
  }

  /**
   * Checks if there are non Dine-in orders during service
   */
  async checkOrdersWithNoEta() {
    if (!this.authentication.Logged_in) return;

    const order: Order = this.docketService.getPendingOrderWithNoETA();
    if (size(order)) {
      // times
      const showYesterday: boolean = this.settings.getBumpScreenSettingByKey('general', 'show_yesterday_orders');
      const timeToUse: string = order.requested_time ? order.requested_time : order.created_time;
      const requestTime: moment.Moment = UtilityClass.parseTimeToTimezone(timeToUse, this.core.Timezone);
      const now: moment.Moment = this.core.Server_time;
      const isIncludedInYesterday: boolean = showYesterday || requestTime.isSameOrAfter(now, 'day');

      if (isIncludedInYesterday) {
        if (!this._etaModalOpen) {
          // ignore the ID once it is viewed
          const viewed = await this.setETASelectorHandler(order, true);

          // returns TRUE if the order is viewed in a MODAL
          if (viewed) {
            this.docketService.removePendingOrderWithNoETA(order.id);
            this.checkOrdersWithNoEta();
          } else {
            // if not viewed because there's already a modal, then just wait for the user to close the modal
          }
        } else {
          // there's an eta modal
          // so do not ask to open
        }
      } else {
        this.docketService.removePendingOrderWithNoETA(order.id);
        this.checkOrdersWithNoEta();
      }
    }
  }

  /**
   * Checks if there are Dine in orders need to be auto printed
   */
  async checkDineinOrdersWithNoEta() {
    if (!this.authentication.Logged_in) return;

    const order: Order = this.docketService.getPendingDineInOrderToAutoPrint();
    if (size(order)) {
      this.docketService.removePendingDineInOrderToAutoPrint(order.id);

      // times
      const showYesterday: boolean = this.settings.getBumpScreenSettingByKey('general', 'show_yesterday_orders');
      const now: moment.Moment = this.core.Server_time;
      const timeToUse: string = order.requested_time ? order.requested_time : order.created_time;
      const requestTime: moment.Moment = UtilityClass.parseTimeToTimezone(timeToUse, this.core.Timezone);
      const isIncludedInYesterday: boolean = showYesterday
        ? requestTime.isSameOrBefore(now, 'day')
        : requestTime.isSame(now, 'day');

      this.analytics.trackEvent('Auto Print', 'Start:' + order.id, this.authentication.Tracking_key);
      if (isIncludedInYesterday) {
        // ignore the ID once auto printing is attempted
        // will retry on next poll completed
        const printed = await this.autoPrintOrder(order);

        // if failed, make the printed flag to FALSE again
        if (!printed) {
          this.docketService.removePendingDineInOrderToAutoPrint(order.id, false, true);
          this.analytics.trackEvent('Auto Print', 'Failed:' + order.id, this.authentication.Tracking_key, true);
        } else {
          this.analytics.trackEvent('Auto Print', 'Success:' + order.id, this.authentication.Tracking_key, true);
        }

        // for dine in, just check the next order right away
        this.checkDineinOrdersWithNoEta();
      }
    }
  }

  /**
   * Auto prints an order. It will set the ETA automatically and prints the docket once successful.
   *
   * Specifically for Dine_in orders with AUTO_SET_ETA set to FALSE in the backend.
   *
   * @param doc - The order to print
   */
  async autoPrintOrder(doc: Order): Promise<boolean> {
    return new Promise(async resolve => {
      const allowByDevMode: boolean = this.core.DEV_MODE && !this.platform.is('cordova');
      if (!allowByDevMode) {
        // intercept early to prevent setting the ETA
        if (size(this.printer.Available_printers) == 0) {
          this.analytics.trackEvent(
            'Auto Print',
            'No available printers:' + doc.id,
            this.authentication.Tracking_key,
            true,
          );
          resolve(false);
          return;
        }

        // check if there is a printer
        if (!this.printer.Has_active_printers) {
          const alertMessage: string = `There are <span class='emp'>no active printers</span> to print the dockets for <span class='emp'>${doc.docket_display_label}</span>.
                                        <br><span class="printer-status-message">The printers can be activated in the <strong class="emp">Printer Setup</strong>.</span>`;
          this.promptFailedPrinting(doc, alertMessage, []);

          this.analytics.trackEvent(
            'Auto Print',
            'No active printer:' + doc.id,
            this.authentication.Tracking_key,
            true,
          );
          resolve(false);
          return;
        }
      }

      // simulate an eta selector
      const eta_time: string = this.core.Server_time.format('YYYY-MM-DDTHH:mm:ss');

      const success = await this.dispatchEtaService.updateDocketEta(eta_time, doc.id, false);

      if (success) {
        this.analytics.trackEvent(
          'Auto Print',
          'Update ETA Success:' + doc.id + ':' + doc.eta,
          this.authentication.Tracking_key,
        );

        // get the new docket with updated RECEIPT
        const docketToPrint: Order = this.docketService.getOrderById(doc.id);
        const printResp = await this.printDocket(docketToPrint, PRINT_TRIGGER.AUTO_PRINT_DINEIN);
        if (printResp.success || (printResp.data && printResp.data.completion == 'partial')) {
          // SUCCESS - ETA SET AND PRINTED
          resolve(true);
        } else {
          this.analytics.trackEvent(
            'Auto Print',
            'Failed to print:' + doc.id + ' Message:' + printResp.message,
            this.authentication.Tracking_key,
            true,
          );
          resolve(false);
        }
      } else {
        this.analytics.trackEvent('Auto Print', 'Failed to set ETA:' + doc.id, this.authentication.Tracking_key, true);

        // FAILED - SETTING ETA
        resolve(false);
      }
    });
  }

  setActiveOrderBySearch(orderId: string): void {
    if (orderId) {
      const order: Order = this.docketService.getOrderById(orderId);
      if (order) {
        // we need to set the filters

        // bump status
        const bumpStatusFilter: string = order.orderFilterBumpState();
        this.filterService.updateFilterByData('bump', bumpStatusFilter, false);

        // date filter
        const dateFilter: string = order.eta
          ? UtilityClass.getOrderDate(order.eta)
          : UtilityClass.getOrderDate(order.requested_time);
        each(this.filterService.Preorder_weeks, (week: IPreorderWeek, weekIndex: number) => {
          let filterFound: IFilterObject;
          if (size(week.filters)) {
            filterFound = week.filters.find((filter: IFilterObject) => filter.data == dateFilter);
          }

          if (filterFound) {
            this.filterService.Preorder_week_index = weekIndex;
            return false;
          }
        });
        this.filterService.updateFilterByData('preorder', dateFilter, false);

        // fulfillment filter
        const contextFilter: string = order.filter_module;
        this.filterService.updateFilterByData('context', contextFilter, false);

        this.docketService.setActiveOrderById(orderId);
        this.filterService.fireFilterUpdate('context', 'preorder', 'weekchange', 'bump');
      }
    }
  }

  /**
   * Prints all the orders for TODAY
   */
  async printAllOrdersToday() {
    const ordersToPrint: Order[] = this.docketService.getOrdersByDate(this.core.Server_time.format('YYYY-MM-DD'));
    if (size(ordersToPrint) > 0) {
      const orderString: string = size(ordersToPrint) > 1 ? 'orders' : 'order';
      const isAre: string = size(ordersToPrint) > 1 ? 'is' : 'are';

      // check if there is a printer
      const allowByDevMode: boolean = this.core.DEV_MODE && !this.platform.is('cordova');
      if (!allowByDevMode) {
        if (!this.printer.Has_active_printers) {
          const alertMessage: string = `There are <span class='emp'>no active printers</span> to print the dockets.
                                        <br><span class="printer-status-message">The printers can be activated in the <strong class="emp">Printer Setup</strong>.</span>`;
          this.createFailedPrintingAlert(`<br>${alertMessage}`, false);

          this.analytics.trackEvent('Print all orders', 'No active printer', this.authentication.Tracking_key, true);
          return;
        }
      }

      const alert = await this.alertCtrl.create({
        header: 'Print dockets for today',
        message: `There ${isAre} ${size(ordersToPrint)} ${orderString} to print, would you like to continue?`,
        buttons: [
          {
            text: 'Cancel',
          },
          {
            text: 'Continue',
            handler: () => {
              const maxCount: number = size(ordersToPrint);
              const printOrder: Subject<Order> = new Subject();
              const completePrinting: Subject<boolean> = new Subject();
              let index: number = 0;

              const printSub: Subscription = printOrder.subscribe(async (order: Order) => {
                await this.printDocket(order, PRINT_TRIGGER.PRINT_ALL_TODAY);
                if (index < maxCount) {
                  printOrder.next(ordersToPrint[++index]);
                } else {
                  if (printSub) printSub.unsubscribe();
                  completePrinting.next(true);
                }
              });

              printOrder.next(ordersToPrint[index]);
            },
          },
        ],
      });

      this.alertQueue.queueAlert(alert, HubAlertModule.PRINT_TODAY_ORDERS, true);
    } else {
      const alert = await this.alertCtrl.create({
        header: 'Print dockets for today',
        message: `There are no orders to print.`,
        buttons: [
          {
            text: 'Continue',
          },
        ],
      });

      this.alertQueue.queueAlert(alert, HubAlertModule.GENERAL_ALERT, true);
    }
  }

  navigateTo(path: string[], data?: any) {
    this.menu.close();

    let navigationExtras: any = { queryParamsHandling: 'preserve', queryParams: { host: 'hub' } };
    if (data) {
      // merge data to navigation extras?
    }

    // add the query parameter for HUBSPOT
    this.router.navigate(path, navigationExtras);
  }

  navigateByUrl(path: string, data?: any) {
    this.menu.close();
    // add the query parameter for HUBSPOT
    this.router.navigateByUrl(path, data);
  }

  async validatePinGateway(): Promise<boolean> {
    return new Promise(async resolve => {
      if (this.authentication.Admin_pin) {
        const pinModal = await this.modalCtrl.create({
          component: PinModalComponent,
          cssClass: 'pin-modal',
          backdropDismiss: false,
        });

        pinModal.onDidDismiss().then(result => {
          const { data } = result;
          if (data && data.success) {
            resolve(true);
          } else {
            resolve(false);
          }
        });

        await pinModal.present();
      } else {
        resolve(true);
      }
    });
  }

  public get Failed_printed_update_backlog(): { ids: string[] }[] {
    return this._failedPrintedUpdateBacklog;
  }
}
